/*
 * <ghy-story class="ghy-story__scene"> (sections/grig-home-story.liquid, styles in assets/grig-home-story.css).
 * Port of NaturLabs MAIN assets/nl-w2-story.js.
 *
 * Writes one custom property, --p (0..1), on the host: the share of the pinned scene that has been scrolled.
 * The value is eased towards the scroll target with requestAnimationFrame (critically damped lerp 0.14), so wheel
 * steps feel continuous. Scroll and resize listeners exist only while the scene is near the viewport
 * (IntersectionObserver, 50 % root margin). Each frame reads the host rect first and writes afterwards (no layout
 * thrash). Sets data-ready once (CSS switches the word and outro reveal on) and data-outro while the CTA is meant to
 * be interactive. Keyboard focus inside the scene while the outro is still hidden jumps the scene to the point where
 * the outro is complete, so the focus ring is never invisible.
 * Does nothing under reduced motion, with the GRIG theme motion setting off (html[data-grig-motion="false"] or
 * html.gh-motion-off), below 768 x 600 px (phones in normal flow, short landscape screens) or without the scene
 * [data-js] mark: CSS keeps the static layout then. Section reloads in the theme editor reconnect the element, so no extra hooks are needed.
 * Switching to reduced motion while the page is open unbinds the scene completely (observer, listeners, --p,
 * data-ready, data-outro), so a later scroll can never write --p again and the static stage stays whole; switching
 * reduced motion off again rebinds it.
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('ghy-story')) return;

  var root = document.documentElement;
  var mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Same gate as the motion media query in grig-home-story.css: phones (normal flow) and short landscape screens
  // (static layout, copy and CTA never clipped) do not bind at all and wait for the viewport to qualify
  var mqMotion = window.matchMedia('(min-width: 768px) and (min-height: 600px)');
  var EASE = 0.14;

  function motionOff() {
    return (
      mqReduced.matches ||
      root.getAttribute('data-grig-motion') === 'false' ||
      root.classList.contains('gh-motion-off')
    );
  }

  function clamp(value) {
    return Math.min(1, Math.max(0, value));
  }

  function listen(mq, handler) {
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
  }

  function unlisten(mq, handler) {
    if (mq.removeEventListener) mq.removeEventListener('change', handler);
    else if (mq.removeListener) mq.removeListener(handler);
  }

  class GhyStory extends HTMLElement {
    connectedCallback() {
      if (this._bound || motionOff() || !this.hasAttribute('data-js')) return;
      var self = this;
      if (!mqMotion.matches) {
        if (!this._waitViewport) {
          this._waitViewport = function () {
            if (!mqMotion.matches) return;
            unlisten(mqMotion, self._waitViewport);
            self._waitViewport = null;
            self.connectedCallback();
          };
          listen(mqMotion, this._waitViewport);
        }
        return;
      }
      this._bound = true;
      this.active = false;
      this.frame = null;
      this.outro = undefined;
      this.current = this.target();
      this.write(this.current);
      this.setAttribute('data-ready', '');

      this.onScroll = function () { self.queue(); };
      this.onResize = function () { self.queue(); };
      this.onMotionChange = function () {
        if (!mqReduced.matches) return;
        self.unbind();
        self.style.removeProperty('--p');
        self.removeAttribute('data-ready');
        self.removeAttribute('data-outro');
        self.outro = undefined;
        if (!self._waitMotion) {
          self._waitMotion = function () {
            if (mqReduced.matches) return;
            unlisten(mqReduced, self._waitMotion);
            self._waitMotion = null;
            self.connectedCallback();
          };
          listen(mqReduced, self._waitMotion);
        }
      };
      listen(mqReduced, this.onMotionChange);

      this.onFocusIn = function () {
        if (!self.hasAttribute('data-ready') || self.outro) return;
        var rect = self.getBoundingClientRect();
        var viewport = window.innerHeight || root.clientHeight;
        var travel = rect.height - viewport;
        if (travel <= 1) return;
        // 'instant', not 'auto': grig.css sets html { scroll-behavior: smooth }, which would animate the jump and leave
        // the eased value behind the real scroll position
        window.scrollTo({ top: window.scrollY + rect.top + travel * 0.72, behavior: 'instant' });
        self.current = self.target();
        self.write(self.current);
      };
      this.addEventListener('focusin', this.onFocusIn);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          function (entries) {
            if (entries[0] && entries[0].isIntersecting) self.start();
            else self.stop();
          },
          { rootMargin: '50% 0px 50% 0px' }
        );
        this.observer.observe(this);
      } else {
        this.start();
      }
    }

    disconnectedCallback() {
      if (this._waitViewport) unlisten(mqMotion, this._waitViewport);
      this._waitViewport = null;
      if (this._waitMotion) unlisten(mqReduced, this._waitMotion);
      this._waitMotion = null;
      this.unbind();
    }

    unbind() {
      if (!this._bound) return;
      this.stop();
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      if (this.onFocusIn) this.removeEventListener('focusin', this.onFocusIn);
      if (this.onMotionChange) unlisten(mqReduced, this.onMotionChange);
      this._bound = false;
    }

    start() {
      if (this.active || !this._bound || motionOff() || !mqMotion.matches) return;
      this.active = true;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.queue();
    }

    stop() {
      if (!this.active) return;
      this.active = false;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      // Park the scene exactly at its end state so it never freezes half way
      this.current = this.target();
      this.write(this.current);
    }

    target() {
      var rect = this.getBoundingClientRect();
      var viewport = window.innerHeight || root.clientHeight;
      var travel = rect.height - viewport;
      if (travel <= 1) return rect.top <= 0 ? 1 : 0;
      return clamp(-rect.top / travel);
    }

    queue() {
      if (this.frame) return;
      var self = this;
      this.frame = requestAnimationFrame(function () { self.tick(); });
    }

    tick() {
      this.frame = null;
      var goal = this.target();
      var delta = goal - this.current;
      this.current = Math.abs(delta) < 0.0005 ? goal : this.current + delta * EASE;
      this.write(this.current);
      if (this.current !== goal && this.active) this.queue();
    }

    write(value) {
      this.style.setProperty('--p', value.toFixed(4));
      var outro = value > 0.5; // matches --o in grig-home-story.css (visible from 0.42, complete at 0.67)
      if (outro !== this.outro) {
        this.outro = outro;
        this.toggleAttribute('data-outro', outro);
      }
    }
  }

  customElements.define('ghy-story', GhyStory);
})();
